import * as React from "react";
import { Select, MenuItem, FormControl, Box, styled, Chip, OutlinedInput, IconButton, CircularProgress } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import { useFilterStyles } from "../../styles/filterStyles";

const StyledSelect = styled(Select)({
  backgroundColor: '#FFFFFF99',
  borderRadius: '12px',
  minHeight: '48px',
  fontSize: '14px',
  fontFamily: 'inherit',
  '& .MuiOutlinedInput-notchedOutline': {
    border: '1.5px solid #E5E7EB',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: '1.5px solid #E5E7EB',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: '2px solid #3B82F6',
    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
  },
  '& .MuiSelect-select': {
    padding: '12px 14px',
    paddingRight: '40px',
    minHeight: '24px',
  },
  '& .MuiSelect-multiple': {
    padding: '8px 14px',
    paddingRight: '40px',
  },
});

const IconWrapper = styled(Box)({
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  backgroundColor: '#F3F4F6',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    fontSize: '18px',
    color: '#6B7280',
  },
});

const StyledChip = styled(Chip)({
  height: '24px',
  fontSize: '12px',
  margin: '2px',
  backgroundColor: '#F3F4F6',
  color: '#374151',
  '& .MuiChip-deleteIcon': {
    fontSize: '16px',
    color: '#6B7280',
  },
});

interface CampaignTypeFilterProps {
  value: string[];
  onChange: (value: string[]) => void;
  options?: string[];
  onActivate?: () => void;
  level?: number;
  onClearLevel?: (level: number) => void;
  isLoading?: boolean;
}

export const CampaignTypeFilter: React.FC<CampaignTypeFilterProps> = ({
  value,
  onChange,
  options,
  onActivate,
  level,
  onClearLevel,
  isLoading,
}) => {
  const filterStyles = useFilterStyles();
  const campaignOptions = options ?? [];
  // Ensure value is always an array to prevent MUI Select error
  const normalizedValue = Array.isArray(value) ? value : [];

  const handleChange = (event: any) => {
    const selectedValues = event.target.value as string[];
    onChange(selectedValues);
  };

  // Handle chip deletion with proper event handling
  const handleChipDelete = (campaignToDelete: string) => (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    const newValue = normalizedValue.filter((item) => item !== campaignToDelete);
    onChange(newValue);
  };

  const renderValue = (selected: string[]) => {
    if (selected.length === 0) {
      return <span style={{ color: '#9CA3AF' }}>Select campaigns</span>;
    }
    
    return (
      <Box 
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          maxWidth: '100%',
          maxHeight: 96,
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingRight: '48px',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
        onClick={(e) => e.stopPropagation()} // Prevent clicks on chips container from opening dropdown
      >
        {selected.map((campaign) => (
          <StyledChip
            key={campaign}
            label={campaign.length > 50 ? `${campaign.substring(0, 50)}...` : campaign}
            size="small"
            onDelete={handleChipDelete(campaign)}
            onMouseDown={(e) => e.stopPropagation()} // Critical: prevent Select from opening
          />
        ))}
      </Box>
    );
  };

  const handleClearLevel = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (level && onClearLevel) {
      onClearLevel(level);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <label className={filterStyles.labelText}>Campaign{level ? ` (${level})` : ""}</label>
        {level && onClearLevel && (
          <IconButton
            size="small"
            onClick={handleClearLevel}
            sx={{
              padding: '2px',
              color: '#6B7280',
              '&:hover': {
                color: '#EF4444',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: '16px' }} />
          </IconButton>
        )}
      </Box>
      <FormControl fullWidth>
        <StyledSelect
          multiple
          value={normalizedValue}
          onChange={handleChange}
          onOpen={onActivate}
          input={<OutlinedInput />}
          renderValue={renderValue}
          displayEmpty
          IconComponent={() => (
            <Box sx={{ 
              position: 'absolute', 
              right: '8px', 
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
            }}>
              <IconWrapper>
                <KeyboardArrowDownIcon />
              </IconWrapper>
            </Box>
          )}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 200,
              },
            },
          }}
        >
          {isLoading ? (
            <MenuItem disabled>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
                <CircularProgress size={16} />
                <span>Loading options...</span>
              </Box>
            </MenuItem>
          ) : campaignOptions.length === 0 ? (
            <MenuItem disabled>No options available</MenuItem>
          ) : (
            campaignOptions.map((campaign) => (
              <MenuItem key={campaign} value={campaign}>
                {campaign}
              </MenuItem>
            ))
          )}
        </StyledSelect>
      </FormControl>
    </>
  );
};

export default CampaignTypeFilter;